const VALID_SECTIONS = ["service", "customer", "internal"];

/**
 * Normalizes FAQ topic objects from index.json. Ensures each topic has a valid `section`.
 * @param {Array<{ title?: string, urlKey?: string, file?: string, section?: string, description?: string }>} rawTopics
 * @returns {Array<{ ...topic, section: "service" | "customer" | "internal" }>}
 */
export function normalizeTopics(rawTopics) {
  const list = Array.isArray(rawTopics) ? rawTopics : [];
  return list.map((topic, index) => {
    const section = topic?.section;
    const valid =
      typeof section === "string" && VALID_SECTIONS.includes(section);
    const normalizedSection = valid ? section : "service";
    if (!valid && typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
      console.warn(
        `[FAQ] Topic at index ${index} (urlKey: ${topic?.urlKey ?? "?"}) has missing or invalid "section". Using "service".`
      );
    }
    return { ...topic, section: normalizedSection };
  });
}

/**
 * @param {Array<{ section: string }>} topics
 * @param {string} section
 * @returns {Array}
 */
export function filterTopicsBySection(topics, section) {
  return topics.filter((t) => (t?.section || "service") === section);
}

/**
 * Deduplicate topics by urlKey. When the same urlKey appears in both "service" and "customer",
 * keep only the "customer" record. Use for Admin and customerType 7 so duplicates show once under customer.
 * @param {Array<{ urlKey?: string, section?: string }>} topics
 * @returns {Array} one topic per urlKey, preferring section "customer"
 */
export function dedupeTopicsByUrlKeyPreferCustomer(topics) {
  const byKey = new Map();
  for (const t of topics) {
    const key = t?.urlKey ?? "";
    const existing = byKey.get(key);
    const isCustomer = (t?.section || "service") === "customer";
    const existingIsCustomer = existing && (existing?.section || "service") === "customer";
    if (!existing || (isCustomer && !existingIsCustomer)) {
      byKey.set(key, t);
    }
  }
  return Array.from(byKey.values());
}
